/**
 * FeedbackBadge — presentational Megaphone indicator showing the total
 * feedback count on a field/group/list. Orange when something is
 * unresolved; muted when everything is handled. Renders nothing when
 * total is 0.
 *
 * Reusable outside the FieldFeedbackContext (e.g. table columns showing
 * aggregate counts on a list summary).
 */

import { cn } from '@/lib/utils';
import { Megaphone } from 'lucide-react';

export interface FeedbackBadgeProps {
  unhandled: number;
  total: number;
  /** Smaller variant for dense rows. Default: regular. */
  size?: 'regular' | 'small';
}

export function FeedbackBadge({
  unhandled,
  total,
  size = 'regular',
}: FeedbackBadgeProps) {
  if (total === 0) return null;

  const hasUnhandled = unhandled > 0;
  const iconSize = size === 'small' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px]',
        'font-semibold tabular-nums',
        hasUnhandled
          ? 'bg-orange-100 text-orange-700'
          : 'bg-muted text-muted-foreground',
      )}
      title={
        hasUnhandled
          ? `${total} feedback item${total === 1 ? '' : 's'} (${unhandled} unresolved)`
          : `${total} feedback item${total === 1 ? '' : 's'}, all handled`
      }
      aria-label={
        hasUnhandled
          ? `${total} feedback items, ${unhandled} unresolved`
          : `${total} feedback items, all handled`
      }
    >
      <Megaphone className={iconSize} />
      {total}
    </span>
  );
}
